<script setup>
import { computed, nextTick, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import FlowNode from './FlowNode.vue';
import { normalizeConditionData } from './conditionFields';
import { useFlowCatalog } from './useFlowCatalog';
import {
  NODE_WIDTH,
  HEADER_HEIGHT,
  createNode,
  nodeHeight,
  nodeOutputs,
  edgePath,
  collectDescendants,
  validateFlow,
} from './flowHelper';

const props = defineProps({
  automation: { type: Object, default: null },
  template: { type: Object, default: null },
  saving: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'save']);

const { t } = useI18n();
const isRTL = useMapGetter('accounts/isRTL');
const { load, context, groupsFor, actionOptionsFor } = useFlowCatalog();

const MIN_SCALE = 0.3;
const MAX_SCALE = 1.5;
const PLUS_OFFSET = 28;
const CHILD_GAP = 96;

const name = ref(
  props.automation?.name ||
    t(props.template?.titleKey || 'AUTOMATION.FLOW.DEFAULT_NAME')
);
const isActive = ref(props.automation?.active ?? true);
const isEditingName = ref(false);
const nameInput = ref(null);
const nameError = ref(false);

const initialTriggerNode = () => {
  const node = createNode('trigger', 0, 0);
  node.data.event = props.template?.event || '';
  return node;
};

const nodes = ref(
  props.automation
    ? JSON.parse(JSON.stringify(props.automation.flow.nodes)).map(node =>
        node.type === 'condition'
          ? { ...node, data: normalizeConditionData(node.data) }
          : node
      )
    : [initialTriggerNode()]
);
const edges = ref(
  props.automation
    ? JSON.parse(JSON.stringify(props.automation.flow.edges))
    : []
);
const view = reactive({ x: 80, y: 120, scale: 1 });
const canvas = ref(null);
const addMenu = ref(null);

const triggerEvent = computed(
  () => nodes.value.find(node => node.type === 'trigger')?.data.event || ''
);

const nodeById = id => nodes.value.find(node => node.id === id);

const startEditingName = async () => {
  isEditingName.value = true;
  await nextTick();
  nameInput.value?.focus();
  nameInput.value?.select();
};

const connections = computed(() => {
  const list = [];
  const plusButtons = [];
  nodes.value.forEach(node => {
    nodeOutputs(node).forEach(({ handle, y }) => {
      const from = { x: node.x + NODE_WIDTH, y: node.y + y };
      const edge = edges.value.find(
        e => e.from === node.id && e.handle === handle
      );
      const target = edge && nodeById(edge.to);
      if (target) {
        const to = { x: target.x, y: target.y + HEADER_HEIGHT / 2 };
        list.push({
          key: `${node.id}-${handle}`,
          edge: { from: node.id, handle },
          from,
          mid: { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 },
          path: edgePath(from, to),
        });
      } else {
        plusButtons.push({
          key: `${node.id}-${handle}`,
          nodeId: node.id,
          handle,
          from,
          x: from.x + PLUS_OFFSET,
          y: from.y,
        });
      }
    });
  });
  return { list, plusButtons };
});

const addMenuItems = computed(() => [
  {
    label: t('AUTOMATION.FLOW.ADD_CONDITION'),
    action: 'condition',
    value: 'condition',
    icon: 'i-lucide-git-fork',
  },
  {
    label: t('AUTOMATION.FLOW.ADD_ACTION'),
    action: 'action',
    value: 'action',
    icon: 'i-lucide-play',
  },
]);

const isFree = (x, y, height) =>
  !nodes.value.some(
    node =>
      Math.abs(node.x - x) < NODE_WIDTH &&
      y < node.y + nodeHeight(node) + 16 &&
      node.y < y + height + 16
  );

const addNode = ({ action: type }) => {
  const { nodeId, handle } = addMenu.value;
  const parent = nodeById(nodeId);
  const output = nodeOutputs(parent).find(o => o.handle === handle);
  const x = parent.x + NODE_WIDTH + CHILD_GAP;
  let y = parent.y + output.y - HEADER_HEIGHT / 2;
  const child = createNode(type, x, y);
  const height = nodeHeight(child);
  while (!isFree(x, y, height)) y += 24;
  child.y = y;

  nodes.value.push(child);
  edges.value.push({ from: nodeId, handle, to: child.id });
  addMenu.value = null;
};

const updateNode = (id, data) => {
  const node = nodeById(id);
  if (!node) return;
  node.data = data;
};

const removeNode = id => {
  nodes.value = nodes.value.filter(node => node.id !== id);
  edges.value = edges.value.filter(edge => edge.from !== id && edge.to !== id);
  addMenu.value = null;
};

const disconnect = ({ from, handle }) => {
  edges.value = edges.value.filter(
    edge => !(edge.from === from && edge.handle === handle)
  );
};

const linking = ref(null);

const toWorld = event => {
  const rect = canvas.value.getBoundingClientRect();
  return {
    x: (event.clientX - rect.left - view.x) / view.scale,
    y: (event.clientY - rect.top - view.y) / view.scale,
  };
};

const connect = (source, handle, targetId) => {
  const target = nodeById(targetId);
  if (!target || target.type === 'trigger' || targetId === source.id) return;
  if (collectDescendants(targetId, edges.value).includes(source.id)) return;
  edges.value = edges.value.filter(
    edge =>
      !(edge.from === source.id && edge.handle === handle) &&
      edge.to !== targetId
  );
  edges.value.push({ from: source.id, handle, to: targetId });
};

const startLink = (event, nodeId, handle, from, onClick = null) => {
  const source = nodeById(nodeId);
  addMenu.value = null;
  linking.value = { from, to: toWorld(event) };
  const move = e => {
    linking.value = { from, to: toWorld(e) };
  };
  const origin = { x: event.clientX, y: event.clientY };
  const stop = e => {
    window.removeEventListener('pointermove', move);
    window.removeEventListener('pointerup', stop);
    linking.value = null;
    const moved = Math.hypot(e.clientX - origin.x, e.clientY - origin.y);
    if (onClick && moved < 4) {
      onClick();
      return;
    }
    const point = toWorld(e);
    const target = nodes.value.find(
      node =>
        point.x >= node.x - 12 &&
        point.x <= node.x + NODE_WIDTH &&
        point.y >= node.y &&
        point.y <= node.y + nodeHeight(node)
    );
    if (target) connect(source, handle, target.id);
  };
  window.addEventListener('pointermove', move);
  window.addEventListener('pointerup', stop);
};

const startDrag = (event, id) => {
  const node = nodeById(id);
  const origin = { px: event.clientX, py: event.clientY, x: node.x, y: node.y };
  const move = e => {
    node.x = origin.x + (e.clientX - origin.px) / view.scale;
    node.y = origin.y + (e.clientY - origin.py) / view.scale;
  };
  const stop = () => {
    window.removeEventListener('pointermove', move);
    window.removeEventListener('pointerup', stop);
  };
  window.addEventListener('pointermove', move);
  window.addEventListener('pointerup', stop);
};

const startPan = event => {
  if (event.button !== 0) return;
  addMenu.value = null;
  const origin = { px: event.clientX, py: event.clientY, ...view };
  const move = e => {
    view.x = origin.x + e.clientX - origin.px;
    view.y = origin.y + e.clientY - origin.py;
  };
  const stop = () => {
    window.removeEventListener('pointermove', move);
    window.removeEventListener('pointerup', stop);
  };
  window.addEventListener('pointermove', move);
  window.addEventListener('pointerup', stop);
};

const zoomTo = (scale, px, py) => {
  const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
  const worldX = (px - view.x) / view.scale;
  const worldY = (py - view.y) / view.scale;
  view.scale = next;
  view.x = px - worldX * next;
  view.y = py - worldY * next;
};

// Listas abertas e áreas roláveis dentro dos nós usam a roda para rolar, não para o zoom.
const scrollsOwnContent = target => {
  if (target.closest('[role="listbox"]')) return true;
  for (let el = target; el && el !== canvas.value; el = el.parentElement) {
    const scrolls = /auto|scroll/.test(getComputedStyle(el).overflowY);
    if (scrolls && el.scrollHeight > el.clientHeight) return true;
  }
  return false;
};

const onWheel = event => {
  if (scrollsOwnContent(event.target)) return;
  event.preventDefault();
  const rect = canvas.value.getBoundingClientRect();
  const factor = event.deltaY < 0 ? 1.1 : 1 / 1.1;
  zoomTo(
    view.scale * factor,
    event.clientX - rect.left,
    event.clientY - rect.top
  );
};

const zoomBy = factor => {
  const rect = canvas.value.getBoundingClientRect();
  zoomTo(view.scale * factor, rect.width / 2, rect.height / 2);
};

const fitView = () => {
  const rect = canvas.value.getBoundingClientRect();
  const minX = Math.min(...nodes.value.map(n => n.x));
  const minY = Math.min(...nodes.value.map(n => n.y));
  const maxX = Math.max(
    ...nodes.value.map(n => n.x + NODE_WIDTH + PLUS_OFFSET)
  );
  const maxY = Math.max(...nodes.value.map(n => n.y + nodeHeight(n)));
  const width = maxX - minX;
  const height = maxY - minY;
  const scale = Math.min(
    1,
    Math.max(
      MIN_SCALE,
      Math.min((rect.width - 160) / width, (rect.height - 160) / height)
    )
  );
  view.scale = scale;
  view.x = (rect.width - width * scale) / 2 - minX * scale;
  view.y = (rect.height - height * scale) / 2 - minY * scale;
};

const showErrors = ref(false);
const autoDeactivated = ref(false);

const fieldGroups = computed(() => groupsFor(triggerEvent.value));

const flowErrors = computed(() => validateFlow(nodes.value, context.value));
const nodesOfType = type => nodes.value.filter(node => node.type === type);

const blocksSave = computed(() => isActive.value && !!flowErrors.value.length);

onMounted(async () => {
  await load();
  // Itens excluídos desde a última edição: marca os nós e mantém desativada.
  const missing = flowErrors.value.some(({ keys }) =>
    keys.some(key => /MISSING|INVALID/.test(key))
  );
  if (missing) {
    showErrors.value = true;
    autoDeactivated.value = true;
    isActive.value = false;
  }
});

const nodeErrors = id =>
  showErrors.value
    ? flowErrors.value.find(({ nodeId }) => nodeId === id)?.keys || []
    : [];

const errorSummary = computed(() =>
  flowErrors.value.flatMap(({ nodeId, keys }) => {
    const { type, data } = nodeById(nodeId);
    const sameType = nodesOfType(type);
    const label = t(`AUTOMATION.FLOW.${type.toUpperCase()}`);
    const position = sameType.findIndex(node => node.id === nodeId) + 1;
    const title =
      data.label?.trim() ||
      (sameType.length > 1 ? `${label} ${position}` : label);
    return keys.map(fullKey => {
      const [key, ruleIndex] = fullKey.split('@');
      const scope =
        ruleIndex === undefined ? '' : ` (${Number(ruleIndex) + 1})`;
      return {
        id: `${nodeId}-${fullKey}`,
        text: `${title}${scope}: ${t(`AUTOMATION.FLOW.ERRORS.${key}`)}`,
      };
    });
  })
);

const onToggleActive = value => {
  if (value && flowErrors.value.length) {
    showErrors.value = true;
    return;
  }
  isActive.value = value;
};

const save = () => {
  if (props.saving) return;
  if (!name.value.trim()) {
    nameError.value = true;
    startEditingName();
    return;
  }
  if (blocksSave.value) {
    showErrors.value = true;
    return;
  }
  emit('save', {
    name: name.value.trim(),
    active: isActive.value,
    flow: { nodes: nodes.value, edges: edges.value },
  });
};
</script>

<template>
  <Teleport to="body">
    <div
      :dir="isRTL ? 'rtl' : 'ltr'"
      class="fixed inset-0 z-[10000] flex bg-n-alpha-black1 backdrop-blur-[2px] p-4"
    >
      <div
        class="flex flex-col w-full h-full overflow-hidden rounded-xl bg-n-background shadow-xl outline outline-1 outline-n-container"
      >
        <div
          class="flex items-center justify-between gap-4 px-6 py-3 border-b border-n-weak"
        >
          <div class="flex items-center gap-2 min-w-0">
            <input
              v-if="isEditingName"
              ref="nameInput"
              v-model="name"
              type="text"
              class="!mb-0 !h-9 w-80 max-w-full text-base font-semibold uppercase rounded-lg border bg-n-solid-1 text-n-slate-12"
              :class="nameError ? 'border-n-ruby-9' : 'border-n-weak'"
              :placeholder="t('AUTOMATION.FLOW.NAME_PLACEHOLDER')"
              @input="nameError = false"
              @blur="isEditingName = false"
              @keydown.enter="isEditingName = false"
            />
            <h2
              v-else
              class="mb-0 text-base font-semibold uppercase truncate text-n-slate-12"
            >
              {{ name || t('AUTOMATION.FLOW.NAME_PLACEHOLDER') }}
            </h2>
            <Button
              icon="i-lucide-pencil"
              color="slate"
              variant="ghost"
              size="sm"
              @mousedown.prevent
              @click="startEditingName"
            />
            <span v-if="nameError" class="text-xs text-n-ruby-11">
              {{ t('AUTOMATION.FLOW.NAME_REQUIRED') }}
            </span>
          </div>
          <div class="flex items-center gap-3">
            <label
              class="flex items-center gap-2 mb-0 text-sm cursor-pointer text-n-slate-12"
            >
              <ToggleSwitch
                :model-value="isActive"
                @update:model-value="onToggleActive"
              />
              {{ t('AUTOMATION.FLOW.ACTIVE') }}
            </label>
            <Button
              :label="t('AUTOMATION.FLOW.CANCEL')"
              color="slate"
              variant="ghost"
              size="sm"
              @click="emit('close')"
            />
            <Button
              :label="
                saving ? t('AUTOMATION.FLOW.SAVING') : t('AUTOMATION.FLOW.SAVE')
              "
              size="sm"
              :is-loading="saving"
              :disabled="saving"
              @click="save"
            />
            <Button
              icon="i-lucide-x"
              color="slate"
              variant="ghost"
              size="sm"
              :title="t('AUTOMATION.FLOW.CLOSE')"
              @click="emit('close')"
            />
          </div>
        </div>

        <div
          ref="canvas"
          class="relative flex-1 overflow-hidden cursor-grab active:cursor-grabbing touch-none bg-[radial-gradient(rgb(var(--slate-6))_1px,transparent_1px)]"
          :style="{
            backgroundSize: `${24 * view.scale}px ${24 * view.scale}px`,
            backgroundPosition: `${view.x}px ${view.y}px`,
          }"
          @pointerdown="startPan"
          @wheel="onWheel"
        >
          <div
            class="absolute left-0 top-0 origin-top-left"
            :style="{
              transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
            }"
          >
            <svg
              class="absolute left-0 top-0 overflow-visible pointer-events-none"
              width="1"
              height="1"
            >
              <path
                v-for="connection in connections.list"
                :key="connection.key"
                :d="connection.path"
                fill="none"
                class="stroke-n-slate-8"
                stroke-width="2"
              />
              <path
                v-if="linking"
                :d="edgePath(linking.from, linking.to)"
                fill="none"
                class="stroke-n-brand"
                stroke-width="2"
                stroke-dasharray="4 3"
              />
              <g v-for="plus in connections.plusButtons" :key="plus.key">
                <line
                  :x1="plus.from.x"
                  :y1="plus.from.y"
                  :x2="plus.x"
                  :y2="plus.y"
                  class="stroke-n-slate-8"
                  stroke-width="2"
                  stroke-dasharray="4 3"
                />
              </g>
              <template v-for="node in nodes" :key="node.id">
                <circle
                  v-for="out in nodeOutputs(node)"
                  :key="`${node.id}-${out.handle}`"
                  :cx="node.x + NODE_WIDTH"
                  :cy="node.y + out.y"
                  r="6"
                  class="fill-n-solid-2 stroke-n-slate-9 cursor-crosshair pointer-events-auto hover:stroke-n-brand"
                  stroke-width="2"
                  @pointerdown.stop.prevent="
                    startLink($event, node.id, out.handle, {
                      x: node.x + NODE_WIDTH,
                      y: node.y + out.y,
                    })
                  "
                />
                <circle
                  v-if="node.type !== 'trigger'"
                  :cx="node.x"
                  :cy="node.y + HEADER_HEIGHT / 2"
                  r="5"
                  class="fill-n-solid-2 stroke-n-slate-9"
                  stroke-width="2"
                />
              </template>
            </svg>

            <FlowNode
              v-for="node in nodes"
              :key="node.id"
              :node="node"
              :trigger-event="triggerEvent"
              :errors="nodeErrors(node.id)"
              :field-groups="fieldGroups"
              :action-options="actionOptionsFor"
              @update="updateNode"
              @remove="removeNode"
              @drag-start="startDrag"
            />

            <button
              v-for="connection in connections.list"
              :key="`cut-${connection.key}`"
              type="button"
              class="absolute flex items-center justify-center !p-0 !m-0 !min-w-0 size-5 -translate-x-1/2 -translate-y-1/2 rounded-full border border-n-slate-8 bg-n-solid-2 text-n-slate-11 opacity-60 hover:opacity-100 hover:bg-n-ruby-3 hover:text-n-ruby-11"
              :style="{
                left: `${connection.mid.x}px`,
                top: `${connection.mid.y}px`,
              }"
              :title="t('AUTOMATION.FLOW.DISCONNECT')"
              @pointerdown.stop
              @click="disconnect(connection.edge)"
            >
              <Icon icon="i-lucide-unlink" class="block size-3 shrink-0" />
            </button>

            <button
              v-for="plus in connections.plusButtons"
              :key="plus.key"
              type="button"
              class="absolute flex items-center justify-center !p-0 !m-0 !min-w-0 size-6 -translate-x-1/2 -translate-y-1/2 rounded-full border border-n-slate-8 bg-n-solid-2 text-n-slate-11 hover:bg-n-brand hover:border-n-brand hover:text-white"
              :style="{ left: `${plus.x}px`, top: `${plus.y}px` }"
              :title="t('AUTOMATION.FLOW.ADD_NODE')"
              @pointerdown.stop.prevent="
                startLink($event, plus.nodeId, plus.handle, plus.from, () => {
                  addMenu = { nodeId: plus.nodeId, handle: plus.handle };
                })
              "
            >
              <Icon icon="i-lucide-plus" class="block size-3.5 shrink-0" />
            </button>

            <template v-if="addMenu">
              <div
                v-for="plus in connections.plusButtons.filter(
                  p =>
                    p.nodeId === addMenu.nodeId && p.handle === addMenu.handle
                )"
                :key="plus.key"
                v-on-clickaway="() => (addMenu = null)"
                class="absolute"
                :style="{ left: `${plus.x + 16}px`, top: `${plus.y - 12}px` }"
                @pointerdown.stop
              >
                <DropdownMenu
                  :menu-items="addMenuItems"
                  class="relative w-44"
                  @action="addNode"
                />
              </div>
            </template>
          </div>

          <div
            v-if="showErrors && flowErrors.length"
            class="absolute top-4 left-4 max-w-sm max-h-48 overflow-y-auto px-3 py-2 text-sm rounded-lg bg-n-ruby-3 text-n-ruby-11"
            @pointerdown.stop
          >
            <p v-if="autoDeactivated" class="mb-2 font-medium">
              {{ t('AUTOMATION.FLOW.AUTO_DEACTIVATED') }}
            </p>
            <ul class="mb-0 list-none m-0 p-0 space-y-1">
              <li v-for="error in errorSummary" :key="error.id">
                {{ error.text }}
              </li>
            </ul>
          </div>

          <div
            class="absolute top-4 right-4 flex items-center gap-2"
            @pointerdown.stop
          >
            <Button
              icon="i-lucide-maximize"
              color="slate"
              variant="faint"
              size="sm"
              :title="t('AUTOMATION.FLOW.FIT')"
              @click="fitView"
            />
            <Button
              icon="i-lucide-plus"
              color="slate"
              variant="faint"
              size="sm"
              :title="t('AUTOMATION.FLOW.ZOOM_IN')"
              @click="zoomBy(1.2)"
            />
            <Button
              icon="i-lucide-minus"
              color="slate"
              variant="faint"
              size="sm"
              :title="t('AUTOMATION.FLOW.ZOOM_OUT')"
              @click="zoomBy(1 / 1.2)"
            />
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
